/**
 * ZSGI ad-measurement library.
 *
 * Responsibilities
 *  1. Capture UTM/gclid/fbclid on the visitor's FIRST landing and persist them
 *     in localStorage so they survive across pages, form-submits, the payment
 *     redirect and the confirmation screen.
 *  2. Dispatch every CPS funnel event to (a) Meta Pixel via fbq, (b) GA4 &
 *     Google Ads via gtag, (c) our own /api/analytics/track for audit + CAPI
 *     mirror.
 *  3. Provide a shared event_id per funnel step so browser fires can dedup
 *     against server-side CAPI fires (Meta requires matching event_id).
 *  4. Read fbp / fbc cookies (dropped by Meta Pixel) and pass them to the
 *     backend so CAPI matching works even when the pixel is loaded.
 *
 * All calls are safe when the pixel/GA IDs are missing — the fbq / gtag
 * stubs in index.html swallow the call and only the internal /analytics/track
 * echo fires. This lets us build & test the funnel before real IDs land.
 */

import { api } from "@/lib/api";

const STORAGE_KEY = "zsgi.attribution.v1";
const SESSION_ID_KEY = "zsgi.session_id.v1";

/* ------------------------------ storage ---------------------------------- */

function safeParse(v) {
    try { return v ? JSON.parse(v) : null; } catch { return null; }
}

export function getStoredAttribution() {
    return safeParse(localStorage.getItem(STORAGE_KEY)) || {};
}

function setStoredAttribution(next) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
}

/* ------------------------------ cookies ---------------------------------- */

function readCookie(name) {
    const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
}

function readGaClientId() {
    // _ga cookie shape: "GA1.2.<client_id>.<timestamp>" — client_id is the middle chunk.
    const ga = readCookie("_ga");
    if (!ga) return null;
    const parts = ga.split(".");
    if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
    return null;
}

/* ------------------------------ capture ---------------------------------- */

const ATTRIBUTION_KEYS = [
    "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
    "gclid", "fbclid",
];

/**
 * Capture attribution from the current URL on FIRST visit only. Subsequent
 * page loads keep the original attribution unless a new gclid/fbclid arrives
 * (paid-traffic best practice: last-click within same session wins).
 */
export function captureAttribution() {
    try {
        const url = new URL(window.location.href);
        const stored = getStoredAttribution();
        const fresh = {};
        ATTRIBUTION_KEYS.forEach((k) => {
            const v = url.searchParams.get(k);
            if (v) fresh[k] = v;
        });

        const isFirst = !stored.first_seen_at;
        const isNewClickId = fresh.gclid || fresh.fbclid;

        const merged = {
            ...stored,
            ...(isFirst ? fresh : {}),
            ...(isNewClickId ? fresh : {}),
            fbp: readCookie("_fbp") || stored.fbp || null,
            fbc: readCookie("_fbc") || stored.fbc || null,
            ga_client_id: readGaClientId() || stored.ga_client_id || null,
        };
        if (isFirst) {
            merged.first_seen_at = new Date().toISOString();
            merged.landing_page = window.location.href;
            merged.referrer = document.referrer || null;
        }
        setStoredAttribution(merged);
        return merged;
    } catch {
        return getStoredAttribution();
    }
}

/* ------------------------------ session ---------------------------------- */

function sessionId() {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
        id = "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
}

/* ------------------------------ dispatch --------------------------------- */

/** Deterministic-ish event_id shared between browser + server for CAPI dedup. */
export function makeEventId(prefix) {
    return `${prefix}-${sessionId()}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Fire an event to Meta Pixel + GA4/gtag + the ZSGI backend audit log.
 * `params` is a free-form payload — value/currency/application_id are used
 * by Meta and GA4 for the paid conversion; the rest is stored for audit.
 */
export async function trackEvent(eventName, params = {}) {
    const attribution = captureAttribution();
    const eventId = params.event_id || makeEventId(eventName.toLowerCase().replace(/[^a-z0-9]+/g, "-"));

    // Meta Pixel — browser side. Using trackCustom for our custom naming.
    // Strip PII (email/phone/name/country) before sending to Meta browser-side.
    // The server-side CAPI mirror /analytics/track still receives PII to
    // enable Meta advanced matching via SHA-256 hashed user_data (Meta guidance).
    try {
        if (window.fbq) {
            const META_SAFE_KEYS = ["value", "currency", "content_ids", "content_name", "content_category", "content_type", "num_items", "program_slug", "application_id", "transaction_id", "source"];
            const metaParams = { content_category: "cps-2026-application" };
            for (const k of META_SAFE_KEYS) if (params[k] !== undefined) metaParams[k] = params[k];
            window.fbq("trackCustom", eventName, metaParams, { eventID: eventId });
        }
    } catch (e) { /* noop */ }

    // Google Analytics 4 + Google Ads — gtag. Strip PII similarly.
    try {
        if (window.gtag) {
            const GTAG_SAFE_KEYS = ["value", "currency", "content_ids", "content_category", "program_slug", "application_id", "transaction_id", "source"];
            const gtagParams = { event_id: eventId, utm_source: attribution.utm_source, utm_medium: attribution.utm_medium, utm_campaign: attribution.utm_campaign };
            for (const k of GTAG_SAFE_KEYS) if (params[k] !== undefined) gtagParams[k] = params[k];
            window.gtag("event", eventName, gtagParams);
            // For Google Ads: fire the conversion send only for the paid event.
            if (
                eventName === "CPS_Application_Complete" &&
                window.__ZSGI_GOOGLE_ADS_CONVERSION__
            ) {
                window.gtag("event", "conversion", {
                    send_to: window.__ZSGI_GOOGLE_ADS_CONVERSION__,
                    value: params.value,
                    currency: params.currency,
                    transaction_id: params.transaction_id || eventId,
                });
            }
        }
    } catch (e) { /* noop */ }

    // Server audit + CAPI mirror
    try {
        await api.post("/analytics/track", {
            event: eventName,
            event_id: eventId,
            program_slug: params.program_slug || "cps-2026",
            email: params.email,
            phone: params.phone,
            full_name: params.full_name,
            country: params.country,
            value: params.value,
            currency: params.currency,
            application_id: params.application_id,
            attribution,
            landing_page: attribution.landing_page || window.location.href,
        });
    } catch (e) { /* silent — measurement must never break UX */ }

    return eventId;
}

/* ------------------------------ meta standard events --------------------- */

// Session-scoped set of (event_name + resource) fires — prevents duplicate
// standard Meta events on refresh/back navigation within the same tab.
const META_FIRED_KEY = "zsgi.meta_fired.v1";

function metaAlreadyFired(key) {
    try {
        const raw = sessionStorage.getItem(META_FIRED_KEY);
        const set = raw ? JSON.parse(raw) : {};
        return !!set[key];
    } catch { return false; }
}

function markMetaFired(key) {
    try {
        const raw = sessionStorage.getItem(META_FIRED_KEY);
        const set = raw ? JSON.parse(raw) : {};
        set[key] = Date.now();
        sessionStorage.setItem(META_FIRED_KEY, JSON.stringify(set));
    } catch { /* noop */ }
}

/**
 * Fire a standard Meta Pixel event (fbq('track', 'PageView' | 'ViewContent' |
 * 'CompleteRegistration' | 'InitiateCheckout' | 'Purchase' | ...).
 *
 * `params` is limited to non-PII fields: value, currency, content_ids,
 * content_name, content_category, content_type, num_items. Anything else is
 * dropped before sending to Meta. This is enforced by an allow-list below.
 *
 * `dedupKey` (optional) prevents the same event from firing twice in the same
 * browser tab session — useful for PageView on refresh and ViewContent on
 * back/forward navigation.
 *
 * Returns the eventID used, so callers can send the matching server-side CAPI
 * event with the same ID for Meta dedup.
 */
export function metaTrack(eventName, params = {}, options = {}) {
    const { dedupKey } = options;
    if (dedupKey && metaAlreadyFired(dedupKey)) return null;
    const eventId = options.eventId || makeEventId(eventName.toLowerCase());
    // Strict allow-list — never leak PII to Meta browser-side.
    const allowed = ["value", "currency", "content_ids", "content_name", "content_category", "content_type", "num_items"];
    const safe = {};
    for (const k of allowed) if (params[k] !== undefined) safe[k] = params[k];
    try {
        if (window.fbq) {
            window.fbq("track", eventName, safe, { eventID: eventId });
        }
    } catch { /* noop */ }
    if (dedupKey) markMetaFired(dedupKey);
    return eventId;
}

/* ------------------------------ SPA route-change PageView ---------------- */

let lastMetaPageviewPath = null;

/**
 * Fire a Meta PageView on SPA route change. The base pixel in index.html
 * fires PageView on the initial hard load; this function keeps it firing on
 * every client-side navigation so Meta sees the full user journey.
 */
export function metaPageViewOnRouteChange(path) {
    if (path === lastMetaPageviewPath) return;
    lastMetaPageviewPath = path;
    try {
        if (window.fbq) window.fbq("track", "PageView");
    } catch { /* noop */ }
}



let pixelLoaded = false;
let ga4Loaded = false;
let googleAdsLoaded = false;

async function loadScript(src) {
    return new Promise((resolve) => {
        const s = document.createElement("script");
        s.async = true;
        s.src = src;
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.head.appendChild(s);
    });
}

/**
 * Dynamically boot Meta Pixel + GA4 + Google Ads based on the /analytics/config
 * response. When any of the IDs is missing, that specific pixel is skipped —
 * the funnel keeps working via the server-side audit log alone.
 */
export async function bootPixels() {
    try {
        const { data } = await api.get("/analytics/config");

        // Meta Pixel — skip if the exact base-code snippet in public/index.html
        // has already initialised fbq (recommended: pixel fires on first byte).
        // Otherwise dynamically boot from env config for staging/preview envs.
        if (data.meta_pixel_id && !pixelLoaded && !window.fbq) {
            /* eslint-disable */
            !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version="2.0";n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,"script","https://connect.facebook.net/en_US/fbevents.js");
            /* eslint-enable */
            window.fbq("init", data.meta_pixel_id);
            window.fbq("track", "PageView");
            pixelLoaded = true;
        } else if (window.fbq) {
            pixelLoaded = true;
        }

        // GA4
        if (data.ga4_measurement_id && !ga4Loaded) {
            await loadScript(`https://www.googletagmanager.com/gtag/js?id=${data.ga4_measurement_id}`);
            window.dataLayer = window.dataLayer || [];
            window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
            window.gtag("js", new Date());
            window.gtag("config", data.ga4_measurement_id, { send_page_view: true });
            ga4Loaded = true;
        }

        // Google Ads — piggybacks on gtag; only needs its own config call.
        if (data.google_ads_conversion_id && !googleAdsLoaded) {
            if (!window.gtag) {
                await loadScript(`https://www.googletagmanager.com/gtag/js?id=${data.google_ads_conversion_id}`);
                window.dataLayer = window.dataLayer || [];
                window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
                window.gtag("js", new Date());
            }
            window.gtag("config", data.google_ads_conversion_id);
            if (data.google_ads_conversion_label) {
                window.__ZSGI_GOOGLE_ADS_CONVERSION__ = `${data.google_ads_conversion_id}/${data.google_ads_conversion_label}`;
            }
            googleAdsLoaded = true;
        }
    } catch (e) { /* noop */ }
}
